import type { ManagedCache, CacheOptions, CacheStats } from '@gzl10/nexus-sdk'
import { LRUCache } from './lru-cache.js'

/**
 * Thin wrapper over LRUCache implementing the ManagedCache SDK contract.
 * Adds name tracking and delegates to addRules callback for invalidation.
 */
export class ManagedCacheImpl<T = unknown> implements ManagedCache<T> {
  private cache: LRUCache<T>
  private onAddRules?: (events: string[]) => void

  constructor(
    public readonly name: string,
    options?: CacheOptions,
    onAddRules?: (events: string[]) => void
  ) {
    this.cache = new LRUCache<T>({
      maxEntries: options?.maxEntries,
      defaultTTL: options?.defaultTTL
    })
    this.onAddRules = onAddRules
  }

  async get(key: string): Promise<T | null> {
    return this.cache.get(key)
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    this.cache.set(key, value, ttl)
  }

  async delete(key: string): Promise<boolean> {
    return this.cache.delete(key)
  }

  async deleteByPrefix(prefix: string): Promise<number> {
    return this.cache.deleteByPrefix(prefix)
  }

  async has(key: string): Promise<boolean> {
    return this.cache.has(key)
  }

  async clear(): Promise<void> {
    this.cache.clear()
  }

  getStats(): CacheStats {
    return this.cache.getStats()
  }

  resetStats(): void {
    this.cache.resetStats()
  }

  addInvalidationRules(events: string[]): void {
    this.onAddRules?.(events)
  }

  async prune(): Promise<number> {
    return this.cache.prune()
  }

  async getSize(): Promise<number> {
    return this.cache.size
  }
}
